import React from 'react';
import { Card, CardActionArea, CardContent, CardMedia, Chip, Grid, Stack, Typography } from '@mui/material';
import noImage from '@assets/img/noImage.jpeg';
import { Dish } from '@api/Dishes/Dish';
import { ProductCategory } from '@api/ProductCategories/ProductCategory';
import { currencyFormatter } from '@utils/Numbers';

interface DishesGridProps {
    dishes: Dish[];
    categories: ProductCategory[];
    productCategoryIdSelected?: string;
    // Agotados por sucursal (§4.3): se MARCAN, nunca se bloquea la venta —
    // el staff puede tener razón para vender (queda producto hecho, sustitución, etc.)
    unavailableDishIds: Set<string>;
    onClickDish: (dish: Dish) => void;
}

const DishesGrid: React.FunctionComponent<DishesGridProps> = (props) => {
    const { dishes, categories, productCategoryIdSelected, unavailableDishIds, onClickDish } = props;

    const visibleDishes = productCategoryIdSelected
        ? dishes.filter((dish) => dish.productCategoryId === productCategoryIdSelected)
        : dishes;

    const groupedDishes = productCategoryIdSelected
        ? [{ id: productCategoryIdSelected, name: '', dishes: visibleDishes }]
        : [
              ...categories.map((category) => ({
                  id: category.id,
                  name: category.name,
                  dishes: dishes
                      .filter((dish) => dish.productCategoryId === category.id)
                      .sort((a, b) => a.priority - b.priority)
              })),
              {
                  id: 'sin-categoria',
                  name: 'Sin categoría',
                  dishes: dishes
                      .filter((dish) => !dish.productCategoryId || !categories.some((c) => c.id === dish.productCategoryId))
                      .sort((a, b) => a.priority - b.priority)
              }
          ].filter((group) => group.dishes.length > 0);

    return (
        <Grid container spacing={2} p={2}>
            {groupedDishes.map((group, index) => (
                <React.Fragment key={index}>
                    {group.name && (
                        <Grid item xs={12}>
                            <Typography variant="h6" fontWeight={600}>
                                {group.name}
                            </Typography>
                        </Grid>
                    )}
                    {group.dishes.map((dish) => {
                        const unavailable = !!dish.id && unavailableDishIds.has(dish.id);
                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={dish.id}>
                                <Card variant="outlined" sx={{ height: '100%', padding: 0 }}>
                                    <CardActionArea
                                        onClick={() => onClickDish(dish)}
                                        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                    >
                                        <CardMedia
                                            component="img"
                                            height="160"
                                            image={dish.photoSrc || noImage}
                                            alt={dish.name}
                                            sx={{
                                                objectFit: 'cover',
                                                filter: unavailable ? 'grayscale(85%)' : 'none',
                                                opacity: unavailable ? 0.65 : 1
                                            }}
                                        />
                                        <CardContent sx={{ flexGrow: 1, width: '100%', padding: 1 }}>
                                            <Stack direction="row" spacing={0.5} alignItems="flex-start">
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    flex={1}
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical'
                                                    }}
                                                >
                                                    {dish.name}
                                                </Typography>
                                                {unavailable && <Chip size="small" color="error" label="Agotado" />}
                                            </Stack>
                                            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                                                {currencyFormatter(Number(dish.cost || 0))}
                                            </Typography>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Grid>
                        );
                    })}
                </React.Fragment>
            ))}
        </Grid>
    );
};

export default DishesGrid;
